import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Compass, Send, X, Loader2 } from "lucide-react";
import api from "@/lib/api";

export const TripAdvisor = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMsg }]);
    setLoading(true);
    try {
      const res = await api.post("/chat", { message: userMsg, session_id: sessionId });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I'm having trouble right now. Try browsing our tours at /tours!" }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          data-testid="trip-advisor-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-[60] flex items-center gap-2 px-5 py-3 bg-sandy text-midnight rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 font-manrope text-sm font-medium"
        >
          <Compass className="w-4 h-4" />
          Trip Advisor
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-[60] w-[380px] max-h-[520px] bg-white border border-midnight/10 shadow-2xl flex flex-col" data-testid="trip-advisor-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 bg-midnight text-white">
            <div className="flex items-center gap-2">
              <Compass className="w-4 h-4 text-sandy" />
              <div>
                <p className="font-manrope text-sm font-medium">Trip Advisor</p>
                <p className="font-manrope text-[10px] text-white/50">Powered by AI</p>
              </div>
            </div>
            <button data-testid="trip-advisor-close" onClick={() => setOpen(false)} className="text-white/50 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4 min-h-[300px] max-h-[360px]">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <Compass className="w-8 h-8 text-sandy/30 mx-auto mb-3" />
                <p className="font-manrope text-sm text-midnight/40 mb-1">Welcome to Trip Advisor!</p>
                <p className="font-manrope text-xs text-midnight/30">Tell me what kind of adventure you're looking for in Saint Lucia.</p>
                <div className="flex flex-wrap justify-center gap-2 mt-4">
                  {["Family-friendly tours", "Best for couples", "Adrenaline rush"].map((q) => (
                    <button
                      key={q}
                      data-testid={`quick-prompt-${q.toLowerCase().replace(/\s/g, '-')}`}
                      onClick={() => { setInput(q); }}
                      className="px-3 py-1.5 border border-sandy/20 text-sandy font-manrope text-[11px] hover:bg-sandy/5 transition-colors duration-200"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] px-3.5 py-2.5 font-manrope text-sm leading-relaxed ${
                    msg.role === "user"
                      ? "bg-sandy/10 text-midnight"
                      : "bg-midnight/5 text-midnight/80"
                  }`}
                  data-testid={`chat-msg-${i}`}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-midnight/5 px-3.5 py-2.5">
                  <Loader2 className="w-4 h-4 text-sandy animate-spin" />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="px-4 py-3 border-t border-midnight/5">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                data-testid="trip-advisor-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                placeholder="Ask about tours..."
                className="font-manrope text-sm border-midnight/10"
                disabled={loading}
              />
              <Button
                data-testid="trip-advisor-send"
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                size="icon"
                className="bg-sandy text-midnight hover:bg-sandy/90 shrink-0"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
